import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/browser";
import {
  V2Button,
  V2CircularProgress,
  V2Dialog,
  V2DialogContent,
  V2DialogHeader,
  V2Radio,
  V2RadioGroup,
  V2Switch,
  V2TextField,
} from "@stacklet/ui";
import { Controller, useForm } from "react-hook-form";
import {
  commitLocalUpdate,
  graphql,
  useFragment,
  useMutation,
  useRelayEnvironment,
} from "react-relay";
import { z } from "zod";

import { ErrorAlerts } from "app/components/ErrorAlerts";
import { useAlertContext, useConfig } from "app/contexts";
// TODO(ENG-7940): API explorer preference temporarily disabled — re-enable when
// the GraphiQL/Apollo toggle is brought back.
// import {
//   API_EXPLORER_USER_DATA_NAME,
//   parseApiExplorer,
//   useApiExplorer,
// } from "app/contexts/ApiExplorerContext"; // avoiding barrel export to satisfy relay
import {
  COLOR_SCHEME_USER_DATA_NAME,
  parseColorScheme,
  useTheme,
} from "app/contexts/ThemeContext"; // avoiding barrel export to satisfy relay
import { useFormErrors } from "app/hooks";
import { useProblemsAlert } from "app/hooks/ui/useProblemsAlert";
import {
  UpdateCurrentUserMutation,
  UpsertUserDataMutation,
} from "app/mutations";

import type { PreferencesDialog_query$key } from "./__generated__/PreferencesDialog_query.graphql";
import type { UpdateCurrentUserMutation as UpdateCurrentUserMutationType } from "app/mutations/__generated__/UpdateCurrentUserMutation.graphql";
import type { UpsertUserDataMutation as UpsertUserDataMutationType } from "app/mutations/__generated__/UpsertUserDataMutation.graphql";

interface Props {
  /** Sets the dialog's open state (e.g. to close it after a successful save). */
  openDialog(arg: boolean): void;
  /** Whether the dialog is currently open. */
  isDialogOpen: boolean;
  /** Bootstrap query ref carrying the user's display name and preferences. */
  queryRef: PreferencesDialog_query$key;
}

const displayNameSchema = z.object({
  displayName: z.string().min(1),
});

type DisplayNameForm = z.infer<typeof displayNameSchema>;

export function PreferencesDialog({
  openDialog,
  isDialogOpen,
  queryRef,
}: Props) {
  const config = useConfig();

  const data = useFragment(
    graphql`
      fragment PreferencesDialog_query on Query {
        whoami {
          displayName
        }
        analytics: userData(
          filterElement: {
            single: {
              name: "name"
              operator: "equals"
              value: "analytics_enabled"
            }
          }
        ) {
          edges {
            node {
              value
            }
          }
          ...useProblemsAlert_problems
        }
      }
    `,
    queryRef,
  );

  useProblemsAlert(data.analytics);

  const analyticsEnabled = Boolean(
    (data.analytics.edges ?? []).find((edge) => edge.node.value === "true"),
  );

  // Separate hooks so each action's in-flight state is independent — otherwise
  // changing the color scheme would flip the analytics switch to a spinner.
  // Declared ahead of the handlers below so each handler references an
  // already-initialized mutation rather than relying on hoisting.
  const [commitAnalytics, isAnalyticsInFlight] =
    useMutation<UpsertUserDataMutationType>(UpsertUserDataMutation);
  const [commitColorScheme] = useMutation<UpsertUserDataMutationType>(
    UpsertUserDataMutation,
  );
  // TODO(ENG-7940): API explorer preference temporarily disabled.
  // const [commitApiExplorer] = useMutation<UpsertUserDataMutationType>(
  //   UpsertUserDataMutation,
  // );

  const toggleAnalytics = () =>
    commitAnalytics({
      variables: {
        input: {
          name: "analytics_enabled",
          type: "config",
          value: String(!analyticsEnabled),
        },
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    });

  const { colorScheme, setColorScheme } = useTheme();

  const changeColorScheme = (value: string) => {
    const scheme = parseColorScheme(value) ?? "system";
    // Apply immediately for instant feedback, then persist so the preference
    // syncs across devices on next load.
    setColorScheme(scheme);
    commitColorScheme({
      variables: {
        input: {
          name: COLOR_SCHEME_USER_DATA_NAME,
          type: "config",
          value: scheme,
        },
      },
      onError: (err) => {
        Sentry.captureException(err);
      },
    });
  };

  // TODO(ENG-7940): API explorer preference temporarily disabled.
  // const { apiExplorer, setApiExplorer } = useApiExplorer();
  //
  // const changeApiExplorer = (value: string) => {
  //   const explorer = parseApiExplorer(value) ?? "apollo";
  //   // Apply immediately for instant feedback, then persist so the preference
  //   // syncs across devices on next load.
  //   setApiExplorer(explorer);
  //   commitApiExplorer({
  //     variables: {
  //       input: {
  //         name: API_EXPLORER_USER_DATA_NAME,
  //         type: "config",
  //         value: explorer,
  //       },
  //     },
  //     onError: (err) => {
  //       Sentry.captureException(err);
  //     },
  //   });
  // };

  const { alertDispatch } = useAlertContext();

  const [formErrors, setFormErrors] = useFormErrors();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<DisplayNameForm>({
    mode: "onChange",
    resolver: zodResolver(displayNameSchema),
  });

  const environment = useRelayEnvironment();

  const [updateCurrentUser, isUpdatingDisplayName] =
    useMutation<UpdateCurrentUserMutationType>(UpdateCurrentUserMutation);

  const updateDisplayName = (input: DisplayNameForm) => {
    // Skip the mutation (and its success toast) when the name is unchanged, so
    // saving without edits just dismisses the dialog rather than claiming an
    // update that never happened.
    if (input.displayName === (data.whoami.displayName ?? "")) {
      openDialog(false);
      return;
    }

    updateCurrentUser({
      variables: { input: { displayName: input.displayName } },
      onCompleted(response, errors) {
        if (errors) {
          setFormErrors(errors.map((error) => error.message));
          return;
        }
        // `CurrentUser` has no `id`, so Relay cannot merge the mutation result
        // into the root `whoami` record automatically. Copy the new display
        // name across so consumers of `whoami` (e.g. the app header) update
        // immediately. We do this here rather than in an `updater` because the
        // updater always runs once the response is written to the store, even
        // when the response carries GraphQL `errors` — which would leave
        // `whoami` showing a name the server rejected.
        commitLocalUpdate(environment, (store) => {
          const displayName = response.updateCurrentUser?.displayName;
          const whoami = store.getRoot().getLinkedRecord("whoami");
          if (displayName != null && whoami) {
            whoami.setValue(displayName, "displayName");
          }
        });
        alertDispatch({
          message: "Display name updated successfully",
          severity: "success",
        });
        openDialog(false);
      },
      onError: (err) => {
        Sentry.captureException(err);
        setFormErrors([
          "Something went wrong updating your display name. Please try again.",
        ]);
      },
    });
  };

  return (
    <V2Dialog
      data-testid="preferences-dialog"
      isOpen={isDialogOpen}
      onOpenChange={openDialog}
      size="lg"
      isDismissable
    >
      <V2DialogHeader>Preferences</V2DialogHeader>

      <V2DialogContent>
        <V2RadioGroup
          assistiveText="“System” follows your operating system's light or dark setting"
          className="mb-2xl"
          label="Appearance"
          onChange={changeColorScheme}
          orientation="horizontal"
          value={colorScheme}
        >
          <V2Radio value="light">Light</V2Radio>
          <V2Radio value="dark">Dark</V2Radio>
          <V2Radio value="system">System</V2Radio>
        </V2RadioGroup>

        {/* TODO(ENG-7940): API explorer preference temporarily disabled —
            re-enable the GraphiQL/Apollo toggle when ready.
        <V2RadioGroup
          className="mb-2xl"
          label="API explorer"
          onChange={changeApiExplorer}
          orientation="horizontal"
          value={apiExplorer}
        >
          <V2Radio value="apollo">Apollo Sandbox</V2Radio>
          <V2Radio value="graphiql">GraphiQL</V2Radio>
        </V2RadioGroup>
        */}

        {config?.analytics_configurable ? (
          <div className="mb-2xl flex flex-col gap-[.625rem]">
            <span className="text-label-small text-text-secondary">
              Analytics
            </span>
            <div className="flex items-center justify-between gap-xl">
              <span>
                Enable analytics to shape improvements to the Stacklet console
              </span>
              {isAnalyticsInFlight ? (
                <V2CircularProgress size="small" />
              ) : (
                <V2Switch
                  isSelected={analyticsEnabled}
                  onChange={toggleAnalytics}
                />
              )}
            </div>
          </div>
        ) : null}

        <form onSubmit={handleSubmit(updateDisplayName)}>
          <Controller
            control={control}
            defaultValue={data.whoami.displayName ?? ""}
            name="displayName"
            render={({ field, fieldState }) => (
              <V2TextField
                assistiveText="The name used to refer to you across the console"
                className="w-full"
                inputProps={{ placeholder: "Enter your display name" }}
                isInvalid={fieldState.invalid}
                label="Display name"
                {...field}
              />
            )}
          />

          <ErrorAlerts className="mt-lg" messages={formErrors} />

          <div className="mt-lg flex justify-end">
            <V2Button
              isDisabled={!isValid || isUpdatingDisplayName}
              isPending={isUpdatingDisplayName}
              pendingText="Saving…"
              type="submit"
              variant="primary"
            >
              Save
            </V2Button>
          </div>
        </form>
      </V2DialogContent>
    </V2Dialog>
  );
}
